// TODO: remove ts-nocheck and fix warnings and errors on this file
// @ts-nocheck
import { useEffect, useState } from 'react';
import { Box, UnorderedList, ListItem, Text } from '@chakra-ui/react';
import { DocsTemplateContent } from '@types';
import { normalizeString } from '@utils';

type Props = {
  content: DocsTemplateContent['body'];
};

export function TableOfContents({ content }: Props): JSX.Element | null {
  const [activeId, setActiveId] = useState('');
  const { toKebabCase } = normalizeString;

  const callback = entries => {
    const intersections = entries.reduce((acc, entry) => {
      // Add intersecting entries to array
      return entry.isIntersecting ? acc.concat(entry) : acc;
    }, []);

    if (intersections[0]) {
      // Get ID from first intersection entry
      setActiveId(intersections[0]?.target?.id);
    }
  };

  const headingsData = content?.reduce((acc, cur) => {
    if (cur._type === 'block' && ['h2', 'h3'].includes(cur.style)) {
      const text = cur.children[0]?.text;

      if (text) {
        acc.push({
          id: toKebabCase(text),
          element: cur.style,
          text
        });
      }
    }
    return acc;
  }, []);

  useEffect(() => {
    let observer: IntersectionObserver;

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(callback);

      headingsData.forEach(item => {
        const heading = document.getElementById(item.id);
        observer.observe(heading);
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [headingsData]);

  if (!headingsData.length) {
    // No headings for ToC
    return null;
  }

  return (
    <Box
      as="nav"
      aria-label="Table of Contents"
      bg="var(--bodyBg)"
      pos="sticky"
      top="105px"
      display={{ base: 'none', xl: 'block' }}
      w="280px"
      h="calc(100vh - 120px)"
      pb={12}
      pl={8}
      overflow="auto"
      flexShrink={0}
      sx={{
        msOverflowStyle: 'none' /* IE and Edge */,
        scrollbarWidth: 'none' /* Firefox */,

        '&::-webkit-scrollbar': {
          display: 'none' /* Chrome, Safari and Opera */
        }
      }}
    >
      <UnorderedList>
        {headingsData.map(({ id, element, text }, index) => {
          const isActive = id === activeId;

          return (
            <ListItem
              key={`${id}-${index}`}
              listStyleType="none"
              borderLeft="1px solid"
              borderColor={
                isActive ? 'var(--primaryBlue)' : 'var(--borderColor)'
              }
            >
              <Text
                as="a"
                href={`#${id}`}
                textStyle="xs-medium"
                color={
                  isActive
                    ? 'var(--primaryTextColor)'
                    : 'var(--secondaryTextColor)'
                }
                display="block"
                px={4}
                py={1.5}
                ml={element !== 'h2' ? 4 : 0}
                transition="color 0.2s"
                _hover={{
                  color: 'var(--primaryTextColor)'
                }}
                onClick={event => {
                  event.preventDefault();
                  if (typeof window !== 'undefined') {
                    window.history.pushState(null, '', `#${id}`);
                    window.scroll({
                      top: document.getElementById(id).offsetTop - 80,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                {text}
              </Text>
            </ListItem>
          );
        })}
      </UnorderedList>
    </Box>
  );
}
